
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useVerificationData } from "@/hooks/useVerificationData";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { AlertCircle, Trash2, RefreshCw, CheckCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { isVerificationTrackingEnabled } from "@/services/VerificationService";

export const VerificationManagerTab = () => {
  const { 
    useSessionsQuery, 
    clearOldSessions, 
    clearIncompleteTestSessions,
    clearAllSessions,
    isTrackingEnabled
  } = useVerificationData();
  
  const { 
    data: sessionsData, 
    isLoading, 
    refetch 
  } = useSessionsQuery("all");
  
  const [daysToKeep, setDaysToKeep] = useState(7);
  const [hoursForIncomplete, setHoursForIncomplete] = useState(24);
  
  // Toggle tracking state (for UI display only)
  const [trackingEnabled, setTrackingEnabled] = useState(isVerificationTrackingEnabled);
  
  const handleClearOldSessions = async () => {
    await clearOldSessions.mutateAsync(daysToKeep);
    refetch();
  };
  
  const handleClearIncompleteTestSessions = async () => {
    await clearIncompleteTestSessions.mutateAsync(hoursForIncomplete);
    refetch();
  };

  const handleClearAllSessions = async () => {
    await clearAllSessions.mutateAsync();
    refetch();
  };
  
  const toggleTrackingInLocalStorage = () => {
    try {
      const adminSettings = localStorage.getItem('ADMIN_SETTINGS');
      let settings = adminSettings ? JSON.parse(adminSettings) : {};
      
      if (!settings.testing) {
        settings.testing = {};
      }
      
      settings.testing.enabled = !trackingEnabled;
      localStorage.setItem('ADMIN_SETTINGS', JSON.stringify(settings));
      
      setTrackingEnabled(!trackingEnabled);
      
      // Reload the page to ensure all components respect the new setting
      window.location.reload();
    } catch (error) {
      console.error("Error updating tracking settings:", error);
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Verification Data Management</CardTitle>
        <CardDescription>
          Manage authentication verification debugging data
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="tracking-enabled">Verification Tracking</Label>
            <p className="text-sm text-muted-foreground">
              Enable or disable auth verification tracking
            </p>
          </div>
          <Switch 
            id="tracking-enabled"
            checked={trackingEnabled}
            onCheckedChange={toggleTrackingInLocalStorage}
          />
        </div>
        
        {!trackingEnabled && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Tracking Disabled</AlertTitle>
            <AlertDescription>
              Verification tracking is currently disabled. Enable it to record authentication debugging data.
            </AlertDescription>
          </Alert>
        )}
        
        {process.env.NODE_ENV === 'production' && trackingEnabled && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Warning</AlertTitle>
            <AlertDescription>
              Verification tracking is enabled in production. This will increase database usage and store sensitive debugging data.
            </AlertDescription>
          </Alert>
        )}
        
        <div className="border rounded-md p-4 space-y-4">
          <h3 className="font-medium">Database Cleanup</h3>
          
          <div className="space-y-2">
            <Label htmlFor="days-to-keep">Keep sessions for (days)</Label>
            <div className="flex space-x-2">
              <Input
                id="days-to-keep"
                type="number"
                min="1"
                max="90"
                value={daysToKeep}
                onChange={(e) => setDaysToKeep(Number(e.target.value))}
                className="w-20"
              />
              <Button 
                onClick={handleClearOldSessions} 
                disabled={clearOldSessions.isPending}
                size="sm"
              >
                {clearOldSessions.isPending ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Clear Old Sessions
              </Button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="hours-for-incomplete">Clear incomplete sessions older than (hours)</Label>
            <div className="flex space-x-2">
              <Input
                id="hours-for-incomplete"
                type="number"
                min="1"
                max="72"
                value={hoursForIncomplete}
                onChange={(e) => setHoursForIncomplete(Number(e.target.value))}
                className="w-20"
              />
              <Button 
                onClick={handleClearIncompleteTestSessions}
                disabled={clearIncompleteTestSessions.isPending}
                size="sm"
              >
                {clearIncompleteTestSessions.isPending ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Clear Incomplete Sessions
              </Button>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-end">
              <Button
                variant="destructive"
                size="sm"
                onClick={handleClearAllSessions}
                disabled={clearAllSessions.isPending}
              >
                {clearAllSessions.isPending ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Delete All Sessions
              </Button>
            </div>
          </div>
        </div>
        
        <div className="border rounded-md p-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-medium">Current Sessions</h3>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          {isLoading ? (
            <div className="text-center py-4 text-gray-500">Loading sessions...</div>
          ) : !sessionsData || sessionsData.sessions.length === 0 ? (
            <div className="text-center py-4 text-gray-500">No verification sessions found.</div>
          ) : (
            <div className="space-y-1">
              <p className="text-sm text-gray-500 mb-2">Total sessions: {sessionsData.totalCount}</p>
              <div className="max-h-64 overflow-y-auto space-y-2">
                {sessionsData.sessions.map((session) => (
                  <div key={session.session_id} className="text-xs border p-2 rounded-md">
                    <div className="flex justify-between">
                      <span className="font-medium">{session.label || 'Unlabeled Session'}</span>
                      <span className="flex items-center">
                        {session.success ? (
                          <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
                        ) : (
                          <AlertCircle className="h-3 w-3 text-red-500 mr-1" />
                        )}
                        {session.success ? 'Success' : 'Incomplete'}
                      </span>
                    </div>
                    <div className="text-gray-500 mt-1">
                      <div>Email: {session.user_email || 'None'}</div>
                      <div>Type: {session.type || 'Unknown'}</div>
                      <div>Steps: {session.step_count}</div>
                      <div>Started: {new Date(session.start_timestamp).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
